<template lang="pug">
  .multi-select(:class="{ '--opened': isOpened, '--title-visible': withTitle, '--has-value': currentTitle !== placeholder }")
    .multi-select__label(v-if="label")
      | {{ label }}
    .multi-select__body(@click="onClickInput" :class="{ '--error': error }")
      .multi-select__name
        .multi-select__name-title(v-if="withTitle && currentTitle !== placeholder") {{ placeholder }}
        span {{ currentTitle || placeholder }}

      .multi-select__arrow
        InlineSvg(:src="require('~/assets/img/icons/filter-arrow.svg')")

      select(v-model="selectValue" class="multi-select__select" multiple @input="onSelectOption" @click.stop)
        option(
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :selected="isSelected(option)"
        )
          | {{ option.title }}

    .multi-select__error(v-if="error")
      | {{ error }}

    transition(name="slide-up" duration="200")
      .multi-select__drop(v-if="isOpened")
        .multi-select__drop-body
          .multi-select__drop-body-inner
            .multi-select__drop-item(
              v-for="option in options"
              :key="option.value"
              :class="{'--active': isSelected(option) }"
              @click.prevent="onClickOption(option)"
            )
              CheckboxInput(:value="isSelected(option)")
                | {{ option.title }}
              img.multi-select__drop-item-icon(
                v-if="option.icon"
                :src="option.icon"
                :alt="option.value"
                width="24"
                height="24"
              )

</template>

<script>
import { debounce } from 'throttle-debounce'
import CheckboxInput from '~/components/CheckboxInput/CheckboxInput'

export default {
  name: 'MultiSelect',

  components: { CheckboxInput },

  props: {
    placeholder: {
      type: String,
      default: ''
    },

    value: {
      type: String,
      required: true
    },

    options: {
      type: Array,
      required: true
    },

    withTitle: {
      type: Boolean,
      default: false
    },

    label: {
      type: String,
      default: ''
    },

    error: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      selectValue: [],
      isOpened: false
    }
  },

  computed: {
    currentTitle () {
      if (!this.selectValue?.length) {
        return this.placeholder
      }

      return this.selectValue
        .map((value) => {
          return this.options.find(opt => opt.value === value)?.title
        })
        .join(', ')
    }
  },

  watch: {
    value: {
      handler (value) {
        this.selectValue = value ? value.split(':') : []
        // const option = this.options.find((item) => item.value === value)
        // this.currentValue = option ? value : null
        // this.currentTitle = option ? option.title : this.placeholder
      },
      immediate: true
    }
  },

  beforeDestroy () {
    document.removeEventListener('click', this.onClickOutside)
  },

  mounted () {
    document.addEventListener('click', this.onClickOutside)
    this.onChangeWithDebunce = debounce(2000, this.onChange)
  },

  methods: {
    isSelected (option) {
      return this.selectValue.includes(option.value.toString())
    },

    onClickInput () {
      console.log('isOpened', this.isOpened)
      this.isOpened = !this.isOpened

      if (!this.isOpened) {
        this.$emit('blur')
      }
    },

    onClickOption (option) {
      const index = this.selectValue.indexOf(option.value)

      if (index >= 0) {
        this.selectValue.splice(index, 1)
      } else {
        this.selectValue.push(option.value)
      }

      this.onChangeWithDebunce()
    },

    onSelectOption () {
      this.onChangeWithDebunce()
    },

    onChange () {
      this.$emit('input', this.selectValue.join(':'))
    },

    onClickOutside ({ target }) {
      if (!this.$el.contains(target)) {
        this.isOpened = false
        this.$emit('blur')
      }
    }
  }
}
</script>

<style lang="scss">
.multi-select {
  position: relative;

  .checkbox-input {
    &__text {
      color: #fff;
      cursor: pointer;
    }

    &__fake.--checked {
      border-color: #fff;
    }
  }

  &__label {
    @include caption-small;

    color: $grey20;
    margin-bottom: 16px;
  }

  &__body {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 40px 0 16px;
    border-radius: 8px;
    background: $blue-grey60;
    cursor: pointer;
    transition: background 0.2s $easing;
    height: 48px;
    min-width: 165px;

    &:hover {
      background: $blue-grey40;
    }

    &.--error {
      background: $red60-20;
    }

    .--opened & {
      background: $blue-grey40;
    }

    .--title-visible & {
      height: 56px;
    }
  }

  &__name {
    @include text-display100;

    font-weight: 500;
    display: flex;
    align-items: center;
    color: $white;

    > span {
      @include text-overflow;

      display: block;
      max-width: 189px;
    }

    &-title {
      font-size: 14px;
      color: $primary05;
      position: absolute;
      left: 0;
      top: -11px;
      white-space: nowrap;
      height: 14px;
      line-height: 14px;
    }

    svg.multi-select__sort-arrow {
      margin-right: 8px;
    }

    .--has-value.--title-visible & {
      padding-top: 5px;
      position: relative;
    }
  }

  &__error {
    @include text-display100;

    position: absolute;
    bottom: -22px;
    right: 0;
    font-size: 18px;
    color: $red40;
  }

  &__drop {
    position: absolute;
    z-index: 4;
    max-width: 400px;
    min-width: 100%;
    width: auto;
    border-radius: 8px;
    top: 100%;
    overflow: hidden;
  }

  &__drop-body {
    border: 1px solid $blue-grey30;
    border-radius: 8px;
    margin-top: 8px;
    background: $blue-grey70;
    padding: 8px 0;
  }

  &__drop-body-inner {
    @include scroll;

    padding: 8px 16px;
    max-height: 265px;
  }

  &__drop-item {
    @include heading6;

    background: $blue-grey60;
    cursor: pointer;
    transition: background 0.2s $easing;
    height: 48px;
    padding: 0 58px 0 16px;
    display: flex;
    align-items: center;
    border-radius: 8px;
    color: #fff;
    margin-bottom: 8px;
    white-space: nowrap;
    position: relative;

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      background: $blue-grey40;
    }

    &.--active {
      background: $gradient-primary;
    }

    &-icon {
      position: absolute;
      right: 16px;
      top: 50%;
      margin-top: -12px;
    }
  }

  &__select {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: -1;
    opacity: 0;
    cursor: pointer;
    left: 0;

    @media (min-width: $breakpointMobile) {
      pointer-events: none;
    }
  }

  &__arrow {
    transition: 0.2s $easing;
    position: absolute;
    right: 8px;
    top: 50%;
    margin-top: -10px;
    height: 20px;
    width: 20px;
    display: flex;

    .--opened & {
      transform: rotate(-180deg);

      svg path {
        fill: white;
      }
    }
  }
}
</style>
